import { useMemo, useState } from 'react';

// Third party
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';

// Components
import { hashPassword } from '@mgm/common/utils';
import {
  PasswordValidation,
} from '@mgm/common/components/password';
import {
  GenericHeader,
} from '@mgm/common/components/base';
import { WelcomeWrapper } from '@mgm/common/wrappers';
import { LanguageSelect } from '../../../components';
import {
  PasswordField,
  TCustomFieldOptions,
  TFormInputs,
} from '../../../components/form';

// Redux-toolkit
import { getPasswordValidation } from '../../../store/common/selectors';

// Hooks
import { useAppSelector } from '../../../hooks/useAppStore';
import { usePasswordValidation, useValidation } from '../../../hooks/useValidation';
import { useResetPassAPI } from '../../../hooks/api';
import { useQueryParams } from '../../../hooks/useCommon';

const ResetPassForm = () => {
  const { t } = useTranslation(['welcome', 'inputs']);
  const { mutate: resetMutate, isLoading } = useResetPassAPI();
  const query = useQueryParams();

  const passwordValid = useAppSelector(getPasswordValidation);
  const {
    isEightCharacter,
    isSpecialCharacter,
    isLowercaseChar,
    isMaxCharacter,
    isUppercaseChar,
    isPasswordVerify,
  } = useValidation();

  const { validationItems } = usePasswordValidation(true);

  const methods = useForm<Pick<TFormInputs, TCustomFieldOptions>>({
    mode: 'all',
  });

  const [password, setPassword] = useState('');

  const handleResetPass = () => {
    const data = {
      password: hashPassword(password),
      resetToken: query.get('resetToken') || '',
    };
    resetMutate(data);
  };

  const isFormValid = useMemo(() => {
    const isPasswordValid = Object.values(passwordValid).every(
      (value) => value || value === null,
    );

    return methods.formState.isValid && isPasswordValid;
  }, [methods.formState.isValid, passwordValid]);

  return (
    <WelcomeWrapper>
      <LanguageSelect />
      <GenericHeader
        subtitleProps={{
          children: t('resetPasswordSubtitle'),
          fontSize: 14,
        }}
        titleProps={{
          children: t('createNewPassword'),
        }}
      />
      <form
        noValidate
        onSubmit={methods.handleSubmit(handleResetPass)}
      >
        <Stack spacing={3}>
          <FormProvider {...methods}>
            <PasswordField
              onChange={setPassword}
              isCheckValidation
              rules={{
                isEightCharacter,
                isSpecialCharacter,
                isLowercaseChar,
                isUppercaseChar,
                isMaxCharacter,
              }}
            />
            <PasswordField
              labelText={t('confirmPassword', { ns: 'inputs' })}
              placeholder={t('confirmNewPassword', { ns: 'inputs' })}
              name="confirmPassword"
              isCheckValidation
              rules={{
                isPasswordVerify: (val: string) => isPasswordVerify(password, val),
              }}
            />
            <PasswordValidation validationItems={validationItems} />
          </FormProvider>
        </Stack>
        <Box paddingY={2}>
          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            loading={isLoading}
            disabled={!isFormValid}
            sx={{ my: 2 }}
          >
            {t('resetPassword', { ns: 'welcome' })}
          </LoadingButton>
        </Box>
      </form>
    </WelcomeWrapper>
  );
};

export {
  ResetPassForm,
};
